'use client'

import { useBooking } from '@/contexts/BookingContext'
import Link from 'next/link'
import Image from 'next/image'
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react'
import { SOCIAL_PLATFORMS } from '@/lib/social-platforms'
import { pickLocalizedText } from '@/lib/localized-content'
import { useTranslations, useLocale } from 'next-intl'

/**
 * Payment method icons using SVG files from /public/marketplace/
 *
 * mada FIRST (founder 2026-10-19): the row follows the page direction, so the
 * first entry is the leftmost logo in English and the rightmost in Arabic -
 * the first one a reader sees in both.
 */
const PAYMENT_ICONS: Array<{ src: string; alt: string; height: number }> = [
  { src: '/marketplace/mada.svg', alt: 'mada', height: 32 },
  { src: '/marketplace/visa.svg', alt: 'Visa', height: 36 },
  { src: '/marketplace/mastercard.svg', alt: 'Mastercard', height: 36 },
  { src: '/tabby-verticle.png', alt: 'Tabby', height: 34 },
  { src: '/tamara_verticle.png', alt: 'Tamara', height: 34 },
]

/**
 * Public booking site footer
 */
export function BookingFooter() {
  const t = useTranslations('booking.footer')
  const locale = useLocale()
  const isAr = locale === 'ar'
  const {
    tenantName,
    tenantLogo,
    tenantDescription,
    tenantNameAr,
    tenantBrandName,
    tenantPhone,
    tenantEmail,
    tenantSocialLinks,
    propertyName,
    propertyPhone,
    propertyEmail,
    propertyAddress,
    propertyGoogleMapUrl,
    vatNumber,
    crNumber,
    pdfFooterNote,
    footerPages,
    allowAgencyOnboarding,
    affiliateProgramName,
    hasAboutPage,
  } = useBooking()

  const currentYear = new Date().getFullYear()
  const displayName = tenantBrandName || propertyName || tenantName || 'Hotel'
  // Company name: Arabic for ar locale, English for en locale
  const companyName = isAr ? (tenantNameAr || tenantName) : tenantName

  const contactPhone = propertyPhone || tenantPhone
  const contactEmail = propertyEmail || tenantEmail

  const hasContact = contactPhone || contactEmail || propertyAddress
  const hasBusiness = vatNumber || crNumber
  const hasSocial = tenantSocialLinks && Object.keys(tenantSocialLinks).length > 0

  // Split footer pages into two columns
  const allLinks = [
    ...(hasAboutPage ? [{ href: '/about', label: t('aboutUs') }] : []),
    ...(hasAboutPage ? [{ href: '/faq', label: t('faq') }] : []),
    ...footerPages
      .filter((p) => p.slug !== 'faq')
      .map((p) => ({ href: `/${p.slug}`, label: pickLocalizedText(locale, p.title, p.title_ar) })),
    { href: '/contact', label: t('contactUs') },
    { href: '/manage-reservation', label: t('manageReservation') },
    ...(allowAgencyOnboarding
      ? [{ href: '/affiliate/login', label: affiliateProgramName || t('affiliatePortal') }]
      : []),
  ]
  const midpoint = Math.ceil(allLinks.length / 2)
  const linksCol1 = allLinks.slice(0, midpoint)
  const linksCol2 = allLinks.slice(midpoint)

  return (
    <footer className="mt-auto">
      {/* Main footer */}
      <div className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6">

            {/* Brand column */}
            <div className="sm:col-span-2 lg:col-span-5 space-y-4">
              <div>
                {tenantLogo ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={tenantLogo}
                    alt={displayName}
                    loading="lazy"
                    className="h-14 w-auto object-contain rounded"
                  />
                ) : (
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{displayName.charAt(0)}</span>
                  </div>
                )}
                {companyName && (
                  <p className="text-gray-300 text-sm font-medium mt-2.5" dir={isAr ? 'rtl' : 'ltr'}>{companyName}</p>
                )}
              </div>

              {tenantDescription && (
                <p className="text-[13px] text-gray-400 leading-relaxed max-w-sm">
                  {tenantDescription}
                </p>
              )}

              {hasSocial && (
                <div className="flex flex-wrap gap-2">
                  {SOCIAL_PLATFORMS.filter((p) => tenantSocialLinks![p.key]).map((platform) => {
                    const Icon = platform.icon
                    return (
                      <a
                        key={platform.key}
                        href={tenantSocialLinks![platform.key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={platform.label}
                        className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </a>
                    )
                  })}
                </div>
              )}

              {/* Payment Methods */}
              <div>
                <h4 className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">{t('weAccept')}</h4>
                <div className="flex flex-wrap items-center gap-2.5">
                  {PAYMENT_ICONS.map((icon) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={icon.alt}
                      src={icon.src}
                      alt={icon.alt}
                      loading="lazy"
                      style={{ height: icon.height }}
                      className="w-auto rounded-md bg-white/10 px-2 py-1.5"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Links Column 1 */}
            <div className="lg:col-span-2">
              <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-4">{t('pages')}</h4>
              <ul className="space-y-2.5">
                {linksCol1.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      prefetch={false}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Links Column 2 */}
            {linksCol2.length > 0 && (
              <div className="lg:col-span-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider mb-4 text-transparent">{t('links')}</h4>
                <ul className="space-y-2.5">
                  {linksCol2.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        prefetch={false}
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact & Business */}
            <div className="lg:col-span-3 space-y-5">
              {hasContact && (
                <div>
                  <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-4">{t('contactTitle')}</h4>
                  <ul className="space-y-2.5">
                    {contactPhone && (
                      <li>
                        <a
                          href={`tel:${contactPhone}`}
                          className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          <Phone className="h-4 w-4 shrink-0 text-gray-500" />
                          <span dir="ltr">{contactPhone}</span>
                        </a>
                      </li>
                    )}
                    {contactEmail && (
                      <li>
                        <a
                          href={`mailto:${contactEmail}`}
                          className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          <Mail className="h-4 w-4 shrink-0 text-gray-500" />
                          {contactEmail}
                        </a>
                      </li>
                    )}
                    {propertyAddress && (
                      <li className="flex items-start gap-2.5 text-sm text-gray-400">
                        <MapPin className="h-4 w-4 shrink-0 mt-0.5 text-gray-500" />
                        <span>{propertyAddress}</span>
                      </li>
                    )}
                    {propertyGoogleMapUrl && (
                      <li>
                        <a
                          href={propertyGoogleMapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2.5 text-sm text-primary/80 hover:text-primary transition-colors"
                        >
                          <ExternalLink className="h-4 w-4 shrink-0" />
                          {t('viewOnMaps')}
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {hasBusiness && (
                <div>
                  <h4 className="text-white text-xs font-semibold uppercase tracking-wider mb-3">{t('businessInfo')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {vatNumber && (
                      <div className="flex flex-col items-center gap-2 bg-gray-800/50 rounded-lg p-3">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src="/vat-seeklogo.png" alt="VAT" loading="lazy" className="h-12 w-auto object-contain" />
                        <span className="text-xs text-gray-400 text-center leading-tight">{vatNumber}</span>
                      </div>
                    )}
                    {crNumber && (
                      <div className="flex flex-col items-center gap-2 bg-gray-800/50 rounded-lg p-3">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src="/CR.jpeg" alt="CR" loading="lazy" className="h-12 w-auto object-contain rounded" />
                        <span className="text-xs text-gray-400 text-center leading-tight">{crNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-gray-950 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
            <div className="text-center sm:text-left">
              <p>
                &copy; {currentYear} {displayName}. {t('allRightsReserved')}
              </p>
              {pdfFooterNote && (
                <p className="text-gray-600 mt-0.5">{pdfFooterNote}</p>
              )}
            </div>
            <a
              href="https://www.taskyinn.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-400 hover:text-gray-300 transition-colors"
            >
              <span className="text-xs">{t('poweredBy')}</span>
              <Image
                src="/brnding/logo/with_icon_logo_for_dark_en.svg"
                alt="TaskyInn"
                width={80}
                height={20}
                className="h-4 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
